import { View, Text, StyleSheet, Modal, TouchableOpacity, Platform } from 'react-native';
import { useEffect, useRef, useState } from 'react';
import { X, Camera } from 'lucide-react-native';
import { CameraView } from 'expo-camera';
import { useTheme } from '@/contexts/ThemeContext';

interface Props {
  visible: boolean;
  isRecording: boolean;
  onCameraRef: (ref: any) => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onClose: () => void;
}

export default function CameraModal({
  visible,
  isRecording,
  onCameraRef,
  onStartRecording,
  onStopRecording,
  onClose,
}: Props) {
  const { theme } = useTheme();
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (isRecording) {
      setElapsedSeconds(0);
      timerRef.current = setInterval(() => {
        setElapsedSeconds((s) => s + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setElapsedSeconds(0);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording]);

  return (
    <Modal visible={visible} animationType="slide">
      <View style={styles.cameraContainer}>
        {Platform.OS === 'web' ? (
          <View style={[styles.webCameraNotice, { backgroundColor: theme.colors.surface }]}>
            <Camera size={48} color={theme.colors.textSecondary} />
            <Text style={[styles.webCameraText, { color: theme.colors.text }]}>
              Camera recording is not available on web
            </Text>
            <Text style={[styles.webCameraSubtext, { color: theme.colors.textSecondary }]}>
              Please use the upload option or access this feature on mobile
            </Text>
            <TouchableOpacity
              style={[styles.closeWebCameraButton, { backgroundColor: theme.colors.accent }]}
              onPress={onClose}>
              <Text style={[styles.closeWebCameraText, { color: theme.colors.accentForeground }]}>
                Close
              </Text>
            </TouchableOpacity>
          </View>
        ) : (
          <>
            {visible && (
            <CameraView
              style={styles.camera}
              facing="back"
              mode="video"
              ref={(ref) => onCameraRef(ref)}
            />
            )}

            <View style={styles.cameraControls}>
              <TouchableOpacity
                style={[styles.cameraCloseButton, { backgroundColor: 'rgba(0, 0, 0, 0.5)' }]}
                onPress={onClose}>
                <X size={24} color={theme.colors.text} />
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.recordButton,
                  {
                    backgroundColor: isRecording ? theme.colors.error : theme.colors.accent,
                    borderColor: theme.colors.text,
                  },
                ]}
                onPress={isRecording ? onStopRecording : onStartRecording}>
                <View
                  style={[
                    styles.recordButtonInner,
                    {
                      backgroundColor: isRecording ? 'transparent' : theme.colors.text,
                      borderRadius: isRecording ? 4 : 30,
                    },
                  ]}
                />
              </TouchableOpacity>

              <View style={styles.cameraSpacer} />
            </View>

            {isRecording && (
              <View style={[styles.recordingIndicator, { backgroundColor: theme.colors.error }]}>
                <View style={[styles.recordingDot, { backgroundColor: theme.colors.text }]} />
                <Text style={[styles.recordingText, { color: theme.colors.text }]}>Recording...</Text>
                <Text style={[styles.recordingText, { color: theme.colors.text }]}>{elapsedSeconds}s</Text>
              </View>
            )}
          </>
        )}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  cameraContainer: {
    flex: 1,
    backgroundColor: 'black',
  },
  camera: {
    flex: 1,
  },
  cameraControls: {
    position: 'absolute',
    bottom: 40,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  cameraCloseButton: {
    width: 48,
    height: 48,
    borderRadius: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cameraSpacer: {
    width: 48,
  },
  recordButton: {
    width: 80,
    height: 80,
    borderRadius: 0,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 4,
  },
  recordButtonInner: {
    width: 60,
    height: 60,
  },
  recordingIndicator: {
    position: 'absolute',
    top: 60,
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 4,
    gap: 8,
  },
  recordingDot: {
    width: 12,
    height: 12,
    borderRadius: 4,
  },
  recordingText: {
    fontSize: 16,
    fontWeight: '700',
  },
  webCameraNotice: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  webCameraText: {
    fontSize: 20,
    fontWeight: '700',
    marginTop: 24,
    marginBottom: 12,
    textAlign: 'center',
  },
  webCameraSubtext: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 32,
  },
  closeWebCameraButton: {
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 0,
  },
  closeWebCameraText: {
    fontSize: 16,
    fontWeight: '700',
  },
});
